import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

export const InventoryForm = NiceModal.create(({ id, mode, modalName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					quantityOnHand: yup.number().nullable(),
					quantityForecasted: yup.number().nullable(),
					minInventory: yup.number().min(0).nullable(),
					maxInventory: yup.number().min(0).nullable(),
					productID: yup.number().nullable().required(),
					sku: yup.string().nullable().required()
				})
				.required(),
		[]
	);
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	const { closeModal } = useModalRouter();

	const defaultValues = {
		warehouseID: null,
		branchID: null,
		productID: null,
		quantityForecasted: null,
		quantityOnHand: null,
		minInventory: null,
		maxInventory: null,
		sku: null
	};
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	useEffect(() => {
		if (id) {
			getInventory();
		}
	}, [id]);

	const getInventory = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(WMS_MODULE.GET_INVENTORY, {
				ID: id
			});
			const resetObject = {};

			if (response.status === 200) {
				Object.keys(defaultValues).forEach((key) => {
					resetObject[key] = response.data[key] ?? null;
				});
				reset(resetObject);
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		}
		setIsLoading(false);
	}, [id]);

	const onSubmit = async (data) => {
		setIsLoading(true);

		if (warehouseAutoComplete !== null || branchAutoComplete !== null) {
			try {
				const URL = id ? WMS_MODULE.EDIT_INVENTORY : WMS_MODULE.ADD_INVENTORY;
				const jsonBODY = {
					ID: parseInt(id) || undefined,
					...data
				};

				!jsonBODY.quantityForecasted && (jsonBODY.quantityForecasted = 0);
				!jsonBODY.quantityOnHand && (jsonBODY.quantityOnHand = 0);

				const response = await axios.post(URL, jsonBODY);

				const { status } = response;
				if (status === 200) {
					showSuccess();
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError({
					header: 'error',
					message: 'allReadySameSkuUsing'
				});
			}

			dispatch(updateDynamicListData(DYNAMICLISTS.INVENTORY));
			closeModal();
		} else {
			showError({
				header: 'error',
				message: 'warehouseOrBranchRequired'
			});
		}
		setIsLoading(false);
	};

	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const warehouseAutoComplete = watch('warehouseID');

	const branchAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const branchAutocompleteLabels = useMemo(() => ['Name'], []);
	const branchAutoComplete = watch('branchID');

	const productAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'name', width: 200 },
			{ accessor: 'SKU', width: 200 }
		],
		[]
	);
	const productAutocompleteLabels = useMemo(() => ['name'], []);

	const defaultColSpan = {
		md: 6
	};
	return (
		<Modal fullwidth name={modalName} maxWidth="small" className="modal-inventoryForm">
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'add' ? 'addInventory' : mode === 'edit' ? 'editInventory' : 'viewInventory'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="warehouse" isLoading={isLoading}>
						<AutoComplete
							name="warehouseID"
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
							value={watch(`warehouseID`)}
							disabled={mode !== 'add'}
							setValue={(value) => {
								const warehouseValue = value?.ID ?? null;
								setValue(`warehouseID`, warehouseValue, {
									shouldValidate: true,
									shouldDirty: true
								});
								if (warehouseValue !== null) {
									setValue('branchID', null);
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="branches" isLoading={isLoading}>
						<AutoComplete
							name="branchID"
							autoCompleteName={AUTOCOMPLETE.BRANCHES}
							columns={branchAutocompleteColumns}
							selectionLabelAccessor={branchAutocompleteLabels}
							value={watch(`branchID`)}
							disabled={mode !== 'add'}
							setValue={(value) => {
								const branchValue = value?.ID ?? null;
								setValue('branchID', branchValue, { shouldValidate: true, shouldDirty: true });
								if (branchValue !== null) {
									setValue('warehouseID', null);
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="product" isLoading={isLoading} required>
						<AutoComplete
							name="productID"
							autoCompleteName={AUTOCOMPLETE.PRODUCTS}
							columns={productAutocompleteColumns}
							selectionLabelAccessor={productAutocompleteLabels}
							value={watch('productID')}
							disabled={mode !== 'add'}
							setValue={(value) => {
								setValue('productID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
								if (mode === undefined) {
									setValue('sku', value?.SKU ?? null, { shouldValidate: true, shouldDirty: true });
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="sku" isLoading={isLoading} required>
						<StringInput name="sku" disabled={mode !== 'add'} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="qtyOnHand" isLoading={isLoading}>
						<IntegerInput name="quantityOnHand" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="qtyForecasted" isLoading={isLoading}>
						<IntegerInput name="quantityForecasted" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="minInventory" isLoading={isLoading}>
						<IntegerInput name="minInventory" disabled={mode === 'view'} />
					</FormItem>
					{/* <FormItem colSpan={defaultColSpan} label="maxInventory" isLoading={isLoading}>
						<IntegerInput name="maxInventory" disabled={mode === 'view'} />
					</FormItem> */}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={closeModal} />
				{mode !== 'view' && (
					<Button
						label={id ? 'save' : 'button.add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default InventoryForm;
